import React, { memo, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import MapIcon from '@mui/icons-material/Map';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import mountainImage from '../Images/mountainimage4.jpg';
import { Search, MapPin, CalendarDays, Users, Compass, Sparkles, Globe, Plane } from 'lucide-react';
import {FilterSection} from './FilterSection'
import Tours from './Tours'
import LastMinuteEscapes from './LastMinuteEscapes'
import { KeyUri } from "../key";
import { fetchToursCached } from "./toursApi";

// Start downloading the tour catalogue as soon as this chunk loads, not after first render.
fetchToursCached().catch(() => {});

const GuestCounterRow = ({ label, sublabel, value, onIncrement, onDecrement, min = 0 }) => (
  <div className="flex items-center justify-between py-2.5">
    <div>
      <p className="text-sm font-medium text-slate-800">{label}</p>
      {sublabel && <p className="text-xs text-slate-400">{sublabel}</p>}
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onDecrement}
        disabled={value <= min}
        className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-blue-600 font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-50 transition-colors"
      >
        −
      </button>
      <span className="w-5 text-center text-sm font-semibold text-slate-800">{value}</span>
      <button
        type="button"
        onClick={onIncrement}
        className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-blue-600 font-semibold hover:bg-blue-50 transition-colors"
      >
        +
      </button>
    </div>
  </div>
);

const PACKAGE_TABS = [
  { key: '',        label: 'All Packages',    Icon: Compass },
  { key: 'group',   label: 'Group Escapes',   Icon: Users },
  { key: 'private', label: 'Private Escapes', Icon: Sparkles },
];

const DESTINATION_TABS = [
  { key: '',              label: 'All Destinations', Icon: Globe },
  { key: 'domestic',      label: 'Domestic',         Icon: MapPin },
  { key: 'international', label: 'International',    Icon: Plane },
];

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfWeek = (year, month) => new Date(year, month, 1).getDay();

const formatDisplayDate = (date) => {
  if (!date) return null;
  return {
    dateStr: `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}, ${date.getFullYear()}`,
    weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
  };
};

const CalendarPopover = ({ selectedDate, viewMonth, viewYear, onPrevMonth, onNextMonth, onSelect }) => {
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
const cells = [
  ...Array(firstDay).fill(null),
  ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
];
return (
    <div className="w-64 bg-white rounded-lg shadow-2xl border border-slate-100 p-2.5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={onPrevMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
          ‹
        </button>
        <span className="text-xs font-semibold text-slate-800">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button type="button" onClick={onNextMonth} className="w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">
          ›
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-[10px] font-semibold text-slate-400 py-0.5">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} />;
          const cellDate = new Date(viewYear, viewMonth, d);
          cellDate.setHours(0, 0, 0, 0);
          const isPast = cellDate < today;
          const isToday = cellDate.getTime() === today.getTime();
          const isSelected =
            selectedDate &&
            selectedDate.getFullYear() === viewYear &&
            selectedDate.getMonth() === viewMonth &&
            selectedDate.getDate() === d;

          return (
            <button
              key={d}
              type="button"
              disabled={isPast}
              onClick={() => onSelect(cellDate)}
              className={`h-7 rounded-md text-xs font-medium transition-colors
                ${isPast ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 hover:bg-blue-50'}
                ${isSelected ? '!bg-blue-600 !text-white' : ''}
                ${isToday && !isSelected ? 'border border-blue-400' : ''}`}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const IndexPackages = () => {
   const location = useLocation();
  const params = new URLSearchParams(location.search);
 const urlCategory = (params.get("category") || "all").toLowerCase();
  const [selectedCategory, setSelectedCategory] = useState(urlCategory);
  const [selectedSort, setSelectedSort] = useState("");
   const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
   const [tripType, setTripType] = useState("");
  const [inputValue, setInputValue] = useState("");
const [travelDate, setTravelDate] = useState(new Date().toISOString().slice(0, 10));
const [guests, setGuests] = useState("");
const [isSearching, setIsSearching] = useState(false); 
const [dateOpen, setDateOpen] = useState(false);
const [selectedDate, setSelectedDate] = useState(new Date());
const [viewMonth, setViewMonth] = useState(new Date().getMonth());
const [viewYear, setViewYear] = useState(new Date().getFullYear());

const inputRef = React.useRef(null);
const [guestsOpen, setGuestsOpen] = useState(false);
const [filtersOpen, setFiltersOpen] = useState(false);
const [roomGuests, setRoomGuests] = useState({ rooms: 1, adults: 2, children: 0 });
const [recentSearches, setRecentSearches] = useState([]);
const [suggestions, setSuggestions] = useState([]);
const [suggestOpen, setSuggestOpen] = useState(false);
const [selectedFilter, setSelectedFilter] = useState(null); 
const [activeIdx, setActiveIdx] = useState(-1);
const [destinationType, setDestinationType] = useState(
  (params.get("destination") || "").toLowerCase()
);
const RECENT_SEARCHES_KEY = 'tgh_recent_searches';

// debounced suggestions from /tour-suggest
useEffect(() => {
  const q = inputValue.trim();
  if (q.length < 2 || selectedFilter?.value === inputValue) {
    setSuggestions([]);
    return;
  }
  const ctrl = new AbortController();
  const t = setTimeout(async () => {
    const url = `${KeyUri.BACKEND_URI}/tour-suggest?q=${encodeURIComponent(q)}`;
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSuggestions(Array.isArray(data) ? data : []);
      setSuggestOpen(true);
      setActiveIdx(-1);
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("[suggest] failed:", e);
        setSuggestions([]);
      }
    }
  }, 250);
  return () => { clearTimeout(t); ctrl.abort(); };
}, [inputValue]);


// if the user changes the state carousel / category pills, drop the stale search text
useEffect(() => {
  if (!selectedFilter) return;
  const stillValid =
    (selectedFilter.type === "state" && selectedState === selectedFilter.key) ||
    (selectedFilter.type === "category" && selectedCategory === selectedFilter.key) ||
    selectedFilter.type === "city" ||
    selectedFilter.type === "country";
  if (!stillValid) {
    setSelectedFilter(null);
    setInputValue("");
  }
}, [selectedState, selectedCategory]);

const resetTypedFilter = (f) => {
  if (f?.type === "state") setSelectedState("");
  if (f?.type === "category") setSelectedCategory("all");
};

const scrollToResults = useCallback(() =>
  setTimeout(() => document.getElementById("tours-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100), []);

const pickSuggestion = (s) => {
  resetTypedFilter(selectedFilter);
  setInputValue(s.value);
  setSelectedFilter(s);
  setSuggestOpen(false);
  setIsSearching(true);
  saveRecentSearch(s.value);

  if (s.type === "state")         { setSelectedState(s.key);    setSearchTerm(""); }
  else if (s.type === "category") { setSelectedCategory(s.key); setSearchTerm(""); }
  else                            { setSearchTerm(s.value); }   // city, country
  scrollToResults();
};

const handleSearchClick = () => {
  const typed = inputValue.trim();
  const isPicked = selectedFilter && selectedFilter.value === inputValue;
  if (!isPicked) {
    resetTypedFilter(selectedFilter);
    setSelectedFilter(null);
    setSearchTerm(typed);
  }
  setIsSearching(true);
  saveRecentSearch(typed);
  setSuggestOpen(false);
  scrollToResults();
};

const TYPE_LABELS = { city: "Cities", state: "States", country: "Countries", category: "Categories" };
// "manali", "Manali ", "MANALI" count as the same place -> list it only once per group
const normPlace = (v) => String(v || "").trim().replace(/\s+/g, " ").toLowerCase();
const grouped = ["city", "state", "country", "category"]
  .map((t) => {
    const seen = new Set();
    const items = suggestions
      .filter((s) => s.type === t)
      .filter((s) => {
        const k = normPlace(s.value);
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    return { type: t, items };
  })
  .filter((g) => g.items.length);
const flat = grouped.flatMap((g) => g.items);

// Load recent searches once
useEffect(() => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    setRecentSearches(Array.isArray(stored) ? stored : []);
  } catch {
    setRecentSearches([]);
  }
}, []);

const saveRecentSearch = (rawTerm) => {
  if (!rawTerm || !rawTerm.trim()) return;
  const term = rawTerm.trim().replace(/\s+/g, " ");
  setRecentSearches((prev) => {
    const next = [term, ...prev.filter((t) => t.trim().replace(/\s+/g, " ").toLowerCase() !== term.toLowerCase())].slice(0, 5);
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable — ignore, recent searches just won't persist
    }
    return next;
  });
};

const updateGuestCount = (key, delta, min = 0) => {
  setRoomGuests((prev) => ({
    ...prev,
    [key]: Math.max(min, prev[key] + delta),
  }));
};

 useEffect(() => {
  setSelectedCategory((params.get("category") || "all").toLowerCase());
}, [location.search]);

// IndexPackages.jsx
const prevCategoryRef = React.useRef(urlCategory);

useEffect(() => {
  const category = params.get("category");
  if (!category || category === prevCategoryRef.current) return; // only real changes
  prevCategoryRef.current = category;

  const timeout = setTimeout(() => {
    document.getElementById("tours-section")?.scrollIntoView({ behavior: "instant", block: "start" });
  });
  return () => clearTimeout(timeout);
}, [location.search]);

useEffect(() => {
  // Instant scroll on page load
  if (window.location.hash === '#tours-section') {
    const el = document.getElementById("tours-section");
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 100, // Header offset
        behavior: 'instant' 
      });
    }
  }
}, []);


/*
useEffect(() => {
  let autocomplete;

  const waitForGoogle = setInterval(() => {
    if (window.google && inputRef.current) {
      clearInterval(waitForGoogle);

      autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ["(cities)"],
          fields: ["name", "formatted_address", "geometry"]
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place?.name) {
          setInputValue(place.name);
        }
      });
    }
  }, 200);

  return () => clearInterval(waitForGoogle);
}, []);*/

 const handleSidebarCategoryChange = (category) => {
    // If clicking same pill category → clear all (back to "all")
    if (selectedCategory === category) {
      setSelectedCategory('all');
    } else {
      // Select this category (syncs pill + checkbox)
      setSelectedCategory(category);
    }
  };

    const handleTripTypeChange = useCallback((type) => {
    setTripType((prev) => (prev === type ? "" : type));
  }, []);

  const handleTripTypeSet = useCallback((type) => {
  setTripType(type);   // direct assignment, used for sidebar sync
}, []);

const clearSearch = () => {
  setInputValue("");
  setSearchTerm("");
  setTravelDate("");
setSelectedDate(new Date());
setViewMonth(new Date().getMonth());
setViewYear(new Date().getFullYear());
  setGuests("");
  setSelectedCategory("all");
  setSelectedSort("");
  setSelectedState("");
   setTripType("");
  setDestinationType("");
  setSelectedFilter(null);   
  setSuggestions([]);  
  setIsSearching(false);
};




const handleDestinationPick = useCallback((d) => {
  setSelectedFilter(null);
  if (d.type === "state") {
    setSelectedState(d.key);
    setSearchTerm("");
    setInputValue("");
  } else {
    setSelectedState("");
    setSearchTerm(d.key);
    setInputValue(d.key);
  }
  setIsSearching(true);
  scrollToResults();
}, []);

const handleDestinationClear = useCallback(() => {
  setSelectedState("");
  setSearchTerm("");
  setInputValue("");
  setSelectedFilter(null);
  setIsSearching(false);
}, []);

  return (
  <div className="font-lato pt-[70px] sm:pt-[64px] md:pt-[80px] lg:pt-[80px] bg-[#EEF4FF] text-[#0F2A5C]">

       
<div className="relative min-h-[560px] md:min-h-[620px] flex items-center bg-slate-950">
  <div className="absolute inset-0 overflow-hidden">
    <img
      src={mountainImage}
      alt="Adventure travel"
      fetchpriority="high"
      decoding="async"
      className="w-full h-full object-cover opacity-40"
    />
    <div className="absolute inset-0 bg-black/10" />
    <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#0B2A5B] to-transparent" />
  </div>
<div className="relative z-10 w-full px-4 sm:px-6 md:px-16 pb-6 pt-4 md:pt-8">

    <div className="max-w-4xl mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-[2px] bg-blue-400"></div>
        <span className="text-blue-300 text-xs sm:text-sm tracking-[0.2em] font-medium uppercase">
          PERSONALIZED DISCOVERY
        </span>
      </div>

      <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-serif font-bold leading-[1.05] mb-2">
        Find Journeys that
      </h1>

       <h1 className="bg-gradient-to-r from-blue-400 to-blue-200 bg-clip-text text-transparent text-4xl sm:text-5xl md:text-7xl font-serif italic font-bold leading-[1.1] mb-5 drop-shadow-2xl">
        Define Your Soul
      </h1>

       <p className="text-blue-100 text-sm sm:text-lg max-w-2xl">
        We curate world-class experience for the modern explorer. Authentic stories over generic sightings.
      </p>
    </div>

{/* Search Bar — MMT-style card */}
<div className="relative max-w-5xl">
  <div className="bg-white/95 rounded-2xl shadow-2xl shadow-black/30 ring-1 ring-white/40 overflow-visible">
    {/* Package type tabs */}
    <div className="flex items-center gap-2 px-3 sm:px-4 pt-3 sm:pt-4 pb-3 border-b border-slate-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      {PACKAGE_TABS.map(({ key, label, Icon }) => {
        const active = tripType === key;
        return (
          <button
            key={label}
            type="button"
            onClick={() => handleTripTypeSet(key)}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
              active
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
                : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}

            <div className="hidden sm:block w-px self-stretch bg-slate-200 mx-1" />

      {DESTINATION_TABS.map(({ key, label, Icon }) => {
        const active = destinationType === key;
        return (
          <button
            key={label}
            type="button"
            onClick={() => { setDestinationType(key); setIsSearching(true); scrollToResults(); }}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap transition ${
              active
                ? 'bg-sky-600 text-white shadow-md shadow-sky-600/25'
                : 'bg-slate-100 text-slate-600 hover:bg-sky-50 hover:text-sky-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}

    </div>
    <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-slate-200">

{/* Destination */}
<div className="flex-1 p-3 sm:p-4 lg:p-5 min-w-0 relative">
  <p className="text-blue-600 text-[10px] sm:text-[11px] font-bold tracking-wider uppercase mb-1">
    <MapPin className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />To City / State / Country / Category
  </p>
  <input
    ref={inputRef}
    autoComplete="off"
    type="text"
    placeholder="Where next?"
    value={inputValue}
    onChange={(e) => {
      const val = e.target.value;
      setInputValue(val);
      if (selectedFilter) { resetTypedFilter(selectedFilter); setSelectedFilter(null); }
      if (!val) setSearchTerm("");
    }}
    onFocus={() => setSuggestOpen(true)}
    onKeyDown={(e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flat.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIdx >= 0 && flat[activeIdx]) pickSuggestion(flat[activeIdx]);
        else handleSearchClick();
      }
      else if (e.key === "Escape") setSuggestOpen(false);
    }}
    className="text-slate-900 text-base sm:text-lg font-bold bg-transparent border-none outline-none w-full placeholder:text-slate-300 placeholder:font-semibold"
  />

  {suggestOpen && (grouped.length > 0 || (!inputValue && recentSearches.length > 0)) && (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setSuggestOpen(false)} />
      <div className="absolute z-50 top-full left-0 mt-2 w-full min-w-[280px] max-h-80 overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-100">
        {!inputValue && recentSearches.length > 0 && (
          <>
            <p className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">Recent</p>
            {recentSearches.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => { setInputValue(r); setSearchTerm(r); setIsSearching(true); setSuggestOpen(false); scrollToResults(); }}
                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50"
              >
                {r}
              </button>
            ))}
          </>
        )}
        {grouped.map((g) => (
          <div key={g.type}>
            <p className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
              {TYPE_LABELS[g.type]}
            </p>
            {g.items.map((s) => {
              const idx = flat.indexOf(s);
              return (
                <button
                  key={`${s.type}-${s.key}`}
                  type="button"
                  onClick={() => pickSuggestion(s)}
                  className={`w-full text-left px-4 py-2 text-sm text-slate-800 hover:bg-blue-50 ${idx === activeIdx ? "bg-blue-50" : ""}`}
                >
                  {s.value}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  )}
</div>


      {/* Departure Date */}
      <div className="flex-1 p-3 sm:p-4 lg:p-5 min-w-0">
        <div className="relative">
        <p className="text-blue-600 text-[10px] sm:text-[11px] font-bold tracking-wider uppercase mb-1">
          <CalendarDays className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />Departure Date
        </p>
        <button
          type="button"
          onClick={() => { setDateOpen((o) => !o); setGuestsOpen(false); setFiltersOpen(false); }}
          className="text-left w-full"
        >
          {selectedDate ? (
            <>
              <span className="text-slate-900 text-base sm:text-lg font-bold block">
                {formatDisplayDate(selectedDate).dateStr}
              </span>
              <span className="text-xs text-slate-400">{formatDisplayDate(selectedDate).weekday}</span>
            </>
          ) : (
            <span className="text-slate-300 text-base sm:text-lg font-bold block">Anytime</span>
          )}
        </button>

  {dateOpen && (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setDateOpen(false)} />
      <div className="absolute z-50 top-full left-0 mt-2" onClick={(e) => e.stopPropagation()}>
        <CalendarPopover
          selectedDate={selectedDate}
          viewMonth={viewMonth}
          viewYear={viewYear}
          onPrevMonth={() => {
            if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
            else setViewMonth((m) => m - 1);
          }}
          onNextMonth={() => {
            if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
            else setViewMonth((m) => m + 1);
          }}
          onSelect={(date) => {
            setSelectedDate(date);
            setTravelDate(date.toISOString().slice(0, 10)); // keep travelDate in sync, in case used elsewhere
            setDateOpen(false);
          }}
        />
    </div>
    </>
  )}
  </div>
</div>

    
     {/* Rooms & Guests */}
 <div className="flex-1 p-3 sm:p-4 lg:p-5 min-w-0">
        <div className="relative">
        <p className="text-blue-600 text-[10px] sm:text-[11px] font-bold tracking-wider uppercase mb-1">
          <Users className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />Rooms &amp; Guests
        </p>
        <button
          type="button"
          onClick={() => { setGuestsOpen((o) => !o); setFiltersOpen(false); }}
          className="text-left w-full"
        >
          <span className="text-slate-900 text-base sm:text-lg font-bold block">
            {roomGuests.adults + roomGuests.children} {roomGuests.adults + roomGuests.children === 1 ? 'Guest' : 'Guests'}
          </span>
          <span className="text-xs text-slate-400">{roomGuests.rooms} {roomGuests.rooms === 1 ? 'Room' : 'Rooms'}</span>
        </button>

        {guestsOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setGuestsOpen(false)} />
            <div
              className="absolute z-50 top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-100 p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <GuestCounterRow
                label="Rooms"
                value={roomGuests.rooms}
                min={1}
                onDecrement={() => updateGuestCount('rooms', -1, 1)}
                onIncrement={() => updateGuestCount('rooms', 1)}
              />
              <div className="border-t border-slate-100" />
              <GuestCounterRow
                label="Adults"
                sublabel="Age 12+"
                value={roomGuests.adults}
                min={1}
                onDecrement={() => updateGuestCount('adults', -1, 1)}
                onIncrement={() => updateGuestCount('adults', 1)}
              />
              <div className="border-t border-slate-100" />
              <GuestCounterRow
                label="Children"
                sublabel="Age 0-11"
                value={roomGuests.children}
                min={0}
                onDecrement={() => updateGuestCount('children', -1, 0)}
                onIncrement={() => updateGuestCount('children', 1)}
              />
              <button
                type="button"
                onClick={() => setGuestsOpen(false)}
                className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg py-2"
              >
                Done
              </button>
            </div>
          </>
     )}
      </div>
  </div>

      {/* Search action lives inside the card */}
      <div className="p-3 lg:p-2.5 flex items-stretch lg:w-52 shrink-0">
        <button
          type="button"
          onClick={handleSearchClick}
          className={`w-full min-h-[52px] rounded-xl flex items-center justify-center gap-2 font-bold text-white text-sm sm:text-base shadow-lg shadow-blue-700/30 transition-all duration-300 ${
            isSearching
              ? 'bg-blue-700 hover:bg-blue-800 ring-4 ring-blue-200/70'
              : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
          }`}
        >
          <Search className="w-4 h-4 sm:w-5 sm:h-5" />
          Search Tours
        </button>
      </div>
    </div>
  </div>

  <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-blue-50/90">
    <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-sky-300" />Certified local guides</span>
    <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-sky-300" />24/7 trip support</span>
    <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-sky-300" />Free cancellation on select tours</span>
    <button
      type="button"
      onClick={clearSearch}
      className="ml-auto underline underline-offset-4 decoration-white/40 hover:decoration-white text-white/90 font-semibold"
    >
      Clear search
    </button>
  </div>
</div>
        </div>
      </div>
<LastMinuteEscapes />

<div id="filter-section">           {/* was id="tours-section" */}
  <FilterSection
    selectedCategory={selectedCategory}
    onCategoryChange={setSelectedCategory}
    selectedSort={selectedSort}
    onSortChange={setSelectedSort}
    selectedState={selectedState}
    onStateChange={setSelectedState}
    tripType={tripType}
    onTripTypeChange={handleTripTypeChange}
    searchTerm={searchTerm}
    onDestinationPick={handleDestinationPick}
    onDestinationClear={handleDestinationClear}
  />
</div>

<div id="tours-section" className="bg-[#EEF4FF]">            {/* only one now */}
  <Tours
    selectedCategory={selectedCategory}
    selectedSort={selectedSort}
    searchTerm={searchTerm}
    selectedState={selectedState}
    tripType={tripType}
     destinationType={destinationType}
     onTripTypeChange={handleTripTypeSet} 
  />
</div>




    
      <section className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white py-16 mt-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-sky-400/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300 mb-2">Why travel with us</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Why Choose Our Tour Packages?</h2>
            <p className="text-blue-200 text-sm mt-2">We engineer stress-free, soul-stirring travel experiences.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<MapIcon className="text-white" />}
              title="Diverse Experiences"
              description="From thrilling adventures to peaceful wellness retreats, explore packages that match your travel mood."
            />
            <FeatureCard
              icon={<AccessTimeIcon className="text-white" />}
              title="Seamless Planning"
              description="We take care of every detail — routes, stays, and activities — so you can just relax and explore."
            />
            <FeatureCard
              icon={<StarIcon className="text-white" />}
              title="Trusted Experts"
              description="Led by experienced guides and curated by travel specialists who know every hidden gem."
            />
          </div>
        </div>
      </section>

   
    </div>
  );
};

const FeatureCard = memo(({ icon, title, description }) => (
  <div className="bg-white/10 backdrop-blur ring-1 ring-white/15 rounded-2xl p-8 text-left hover:bg-white/15 hover:-translate-y-1 transition duration-300">
    <div className="bg-sky-500 w-12 h-12 rounded-xl flex items-center justify-center mb-5 shadow-md shadow-sky-900/30">
      {icon}
    </div>
    <h3 className="text-lg font-bold mb-2">{title}</h3>
    <p className="text-blue-100 text-sm leading-relaxed">{description}</p>
  </div>
));

export default IndexPackages;


/*
import React, { memo, useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import MapIcon from '@mui/icons-material/Map';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import Button from '@mui/material/Button';
import mountainImage from '../Images/mountainimage4.jpg';
import { Search } from 'lucide-react';

import TextField from '@mui/material/TextField';
import {FilterSection} from './FilterSection'
import Tours from './Tours'


const IndexPackages = () => {
   const location = useLocation();
  const params = new URLSearchParams(location.search);
 const urlCategory = (params.get("category") || "all").toLowerCase();
  const [selectedCategory, setSelectedCategory] = useState(urlCategory);
  const [selectedSort, setSelectedSort] = useState("");
   const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("");
  
   useEffect(() => {
    setSelectedCategory((params.get("category") || "all").toLowerCase());
    const filterEl = document.getElementById("filter-section");
    if (filterEl) {
      filterEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.search]);

  return (
    <div className="font-lato mt-23 lg:mt-32 ">
       
      <div className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src={mountainImage}
          alt="Adventure travel"
          className="absolute inset-0 w-full h-full object-cover mt-4"
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
         <h1 className="text-white text-5xl mb-4">Explore the World with Us</h1>

          <p className="mb-8 text-white/90 max-w-2xl mx-auto">
            Discover unforgettable destinations, create lasting memories, and embark on the adventure of a lifetime
          </p>
          
          <div className="bg-white rounded-lg p-4 max-w-3xl mx-auto shadow-2xl">
            <div className="flex flex-col md:flex-row gap-3">
              <TextField
                placeholder="Where do you want to go?"
                className="flex-1"
                 value={searchTerm}                        // controlled input
        onChange={(e) => setSearchTerm(e.target.value)} 
              />
              <TextField
                type="date"
                placeholder="Date"
                className="md:w-48"
              />
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Search className="w-5 h-5 mr-2" />
                Search Tours
              </Button>
            </div>
          </div>
        </div>
      </div>


     <FilterSection
  selectedCategory={selectedCategory}
  onCategoryChange={setSelectedCategory}
  selectedSort={selectedSort}
  onSortChange={setSelectedSort}
  selectedState={selectedState}         
  onStateChange={setSelectedState}       
/>

      
      
  <Tours
  selectedCategory={selectedCategory}
  selectedSort={selectedSort}
  searchTerm={searchTerm}
  selectedState={selectedState}         
/>



    
      <section className=" py-8 px-6 bg-transparent ">
        <div className="max-w-7xl mx-auto text-center">
    <h2 className="text-4xl font-bold mb-12 text-blue-900">
  Why Choose Our Tour Packages?
</h2>

<div className="grid md:grid-cols-3 gap-8">
  <FeatureCard
    icon={<MapIcon className="text-blue-800" fontSize="large" />}
    title="Diverse Experiences"
    description="From thrilling adventures to peaceful wellness retreats, explore packages that match your travel mood."
  />
  <FeatureCard
    icon={<AccessTimeIcon className="text-blue-800" fontSize="large" />}
    title="Seamless Planning"
    description="We take care of every detail — routes, stays, and activities — so you can just relax and explore."
  />
  
  <FeatureCard
    icon={<StarIcon className="text-blue-800" fontSize="large" />}
    title="Trusted Experts"
    description="Led by experienced guides and curated by travel specialists who know every hidden gem."
  />
</div>

        </div>
      </section>

   
    </div>
  );
};

const FeatureCard = memo(({ icon, title, description }) => (
  <div
    className="bg-white shadow-md rounded-xl p-8 hover:shadow-xl transition-shadow duration-300 relative cursor-pointer hover:z-10 hover:-translate-y-1"
    style={{ willChange: 'transform, box-shadow' }}
  >
    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
      {icon}
    </div>
    <h3 className="text-2xl font-semibold mb-4 text-blue-900">{title}</h3>
    <p className="text-blue-700">{description}</p>
  </div>
));

export default IndexPackages;

*/

//export default IndexPackages;